// Centralized caption font sizing logic so all UI screens
// (preview modal, live caption renderer, quick setting cards)
// behave consistently.

export const CAPTION_SIZE_PRESETS = {
  S: 24,
  M: 36,
  L: 48,
};

const CAPTION_BASE_MIN_PX = 18;
const CAPTION_MIN_RATIO = 0.82;
const CAPTION_VW_BY_SIZE = {
  S: 5,
  M: 6.5,
  L: 8,
};

export function getCaptionMaxPxFromSizeKey(sizeKey) {
  return CAPTION_SIZE_PRESETS[sizeKey] ?? CAPTION_SIZE_PRESETS.S;
}

export function getCaptionSizeKeyFromMaxPx(fontSizeMaxPx) {
  const maxPx = Number(fontSizeMaxPx);
  if (!Number.isFinite(maxPx)) return "S";

  // Pick the closest preset so older saved values (or non-preset values)
  // still map to a stable S/M/L selection.
  let bestKey = "S";
  let bestDiff = Infinity;

  for (const [key, presetMaxPx] of Object.entries(CAPTION_SIZE_PRESETS)) {
    const diff = Math.abs(presetMaxPx - maxPx);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestKey = key;
    }
  }

  return bestKey;
}

export function getCaptionFontSizeCss(fontSizeMaxPx) {
  const maxPx = Number(fontSizeMaxPx);
  const safeMaxPx = Number.isFinite(maxPx) && maxPx > 0 ? maxPx : CAPTION_SIZE_PRESETS.S;
  const minPx = Math.max(CAPTION_BASE_MIN_PX, Math.round(safeMaxPx * CAPTION_MIN_RATIO));
  const sizeKey = getCaptionSizeKeyFromMaxPx(safeMaxPx);
  const vw = CAPTION_VW_BY_SIZE[sizeKey] ?? CAPTION_VW_BY_SIZE.S;
  return `clamp(${minPx}px, ${vw}vw, ${safeMaxPx}px)`;
}

/** Timestamp / speaker line: scales with caption size (S/M/L), stays clearly smaller than body. */
const META_MAX_RATIO = 0.42;
const META_MIN_RATIO = 0.68;
const META_VW_RATIO = 0.38;

/** Mobile baseline: ~2–3 lines; scaled up on wider viewports */
const CHARS_PER_BLOCK_BY_SIZE = {
  S: 132,
  M: 118,
  L: 102,
};

const CAPTION_COLUMN_MAX_PX = 1152; // max-w-6xl
const MOBILE_BASELINE_WIDTH = 390;

export function getMaxCharsPerCaptionBlock(fontSizeMaxPx, viewportWidth) {
  const key = getCaptionSizeKeyFromMaxPx(fontSizeMaxPx);
  const base = CHARS_PER_BLOCK_BY_SIZE[key] ?? CHARS_PER_BLOCK_BY_SIZE.S;

  const vw =
    Number.isFinite(viewportWidth) && viewportWidth > 0
      ? viewportWidth
      : MOBILE_BASELINE_WIDTH;
  const columnWidth = Math.min(
    Math.max(vw - 40, 300),
    CAPTION_COLUMN_MAX_PX,
  );
  const widthScale = Math.min(
    Math.max(columnWidth / MOBILE_BASELINE_WIDTH, 1),
    2.15,
  );

  return Math.round(base * widthScale);
}

export function getCaptionMetaFontSizeCss(fontSizeMaxPx) {
  const maxPx = Number(fontSizeMaxPx);
  const safeMaxPx = Number.isFinite(maxPx) && maxPx > 0 ? maxPx : CAPTION_SIZE_PRESETS.S;
  const sizeKey = getCaptionSizeKeyFromMaxPx(safeMaxPx);
  const captionVw = CAPTION_VW_BY_SIZE[sizeKey] ?? CAPTION_VW_BY_SIZE.S;
  const metaMax = Math.max(12, Math.round(safeMaxPx * META_MAX_RATIO));
  const metaMin = Math.max(10, Math.round(metaMax * META_MIN_RATIO));
  const metaVw = Number((captionVw * META_VW_RATIO).toFixed(2));
  return `clamp(${metaMin}px, ${metaVw}vw, ${metaMax}px)`;
}

